import { useState, useCallback, useRef } from 'react';
import { Upload, CheckCircle, XCircle } from 'lucide-react';
import api from '../../api/axios';

export default function ImportDropzone({ onImportComplete }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['json', 'html', 'htm'].includes(ext)) {
      setError('Unsupported file type. Use .json or .html');
      return;
    }

    setUploading(true);
    setError(null);
    setResult(null);
    setProgress({ phase: 'uploading', message: 'Uploading file...' });

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Step 1: Upload file — backend returns { jobId } immediately
      const uploadRes = await api.post('/registrations/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { jobId } = uploadRes.data;
      if (!jobId) throw new Error('No jobId returned');

      setProgress({ phase: 'parsing', message: 'Parsing file...' });

      // Step 2: Poll for progress every 500ms
      await new Promise((resolve, reject) => {
        pollRef.current = setInterval(async () => {
          try {
            const statusRes = await api.get(`/registrations/import/status/${jobId}`);
            const job = statusRes.data;

            if (job.error) {
              stopPolling();
              reject(new Error(job.error));
              return;
            }

            if (job.phase === 'importing') {
              setProgress({
                phase: 'importing',
                processed: job.processed,
                total: job.total,
                inserted: job.inserted,
                updated: job.updated,
                failed: job.failed,
              });
            } else if (job.phase === 'parsing') {
              setProgress({ phase: 'parsing', message: 'Parsing file...' });
            }

            if (job.done) {
              stopPolling();
              resolve(job);
            }
          } catch (pollErr) {
            stopPolling();
            reject(new Error('Lost connection while importing. The import may still be running on the server.'));
          }
        }, 500);
      }).then((job) => {
        setResult({
          total: job.total,
          inserted: job.inserted,
          updated: job.updated,
          failed: job.failed,
          errors: job.errors,
        });
        setProgress(null);
        if (onImportComplete) onImportComplete(job);
      });
    } catch (err) {
      stopPolling();
      setError(err.response?.data?.error || err.message || 'Import failed');
      setProgress(null);
    } finally {
      setUploading(false);
    }
  }, [onImportComplete]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const onFileSelect = (e) => {
    handleFile(e.target.files[0]);
    e.target.value = '';
  };

  const pct = progress && progress.total ? Math.round((progress.processed / progress.total) * 100) : null;

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`relative border-2 border-dashed rounded-xl p-8 sm:p-12 text-center transition-colors min-h-[200px] flex flex-col items-center justify-center ${
          dragging ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-gray-400'
        } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
      >
        <input
          type="file"
          accept=".json,.html,.htm"
          onChange={onFileSelect}
          className="absolute inset-0 opacity-0 cursor-pointer"
        />
        <Upload className={`w-10 h-10 mb-3 ${dragging ? 'text-teal-500' : 'text-gray-400'}`} />
        <p className="text-gray-700 font-medium">
          {uploading ? 'Importing...' : 'Drop file here or click to upload'}
        </p>
        <p className="text-sm text-gray-500 mt-1">Accepts .html and .json files</p>
      </div>

      {uploading && progress && (
        <div className="mt-4">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full bg-teal-500 rounded-full transition-[width] duration-300 ${pct == null ? 'animate-pulse' : ''}`}
              style={{ width: pct != null ? `${pct}%` : '70%' }}
            />
          </div>
          <div className="flex items-center justify-between mt-2 text-sm">
            <span className="text-gray-600">
              {progress.phase === 'uploading' && 'Uploading file...'}
              {progress.phase === 'parsing' && 'Parsing file...'}
              {progress.phase === 'importing' && (
                <>
                  Importing <strong>{(progress.processed || 0).toLocaleString()}</strong> /{' '}
                  <strong>{(progress.total || 0).toLocaleString()}</strong> records
                  {pct != null && <span className="text-gray-400"> ({pct}%)</span>}
                </>
              )}
            </span>
            {progress.phase === 'importing' && (
              <span className="text-xs text-gray-500">
                <span className="text-green-600">+{progress.inserted || 0}</span>{' '}
                <span className="text-blue-600">~{progress.updated || 0}</span>{' '}
                <span className="text-red-500">x{progress.failed || 0}</span>
              </span>
            )}
          </div>
        </div>
      )}

      {result && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
          <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
            <CheckCircle className="w-5 h-5" /> Import Complete
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><span className="text-gray-500">Total:</span> <strong>{result.total}</strong></div>
            <div><span className="text-gray-500">Inserted:</span> <strong className="text-green-600">{result.inserted}</strong></div>
            <div><span className="text-gray-500">Updated:</span> <strong className="text-blue-600">{result.updated}</strong></div>
            <div><span className="text-gray-500">Failed:</span> <strong className="text-red-600">{result.failed}</strong></div>
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-2 text-xs text-red-600">
              {result.errors.slice(0, 5).map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700">
          <XCircle className="w-5 h-5 flex-shrink-0" /> {error}
        </div>
      )}
    </div>
  );
}
